import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, UserCircle2, CheckCircle2 } from 'lucide-react';
import { organizationInviteAPI } from '@/api/organizationInviteApi';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '@/utils/constants';
import { useAppDispatch } from '@/store/hooks';
import { setAuthCredentials, addNameField } from '@/store/authSlice';


interface UserInviteLoginProps {
  tempToken: string;
}

interface FormFields {
  firstName: string;
  lastName: string;
  userName: string;
  password: string;
  confirmPassword: string;
}

type FormErrors = Partial<Record<keyof FormFields, string>>;


const PASSWORD_MIN = 8;

function validate(fields: FormFields): FormErrors {
  const errors: FormErrors = {};

  if (!fields.firstName.trim())
    errors.firstName = 'First name is required.';

  if (!fields.lastName.trim())
    errors.lastName = 'Last name is required.';

  if (!fields.userName.trim())
    errors.userName = 'Username is required.';
  else if (!/^[a-zA-Z0-9_.-]{3,32}$/.test(fields.userName.trim()))
    errors.userName = 'Username must be 3–32 chars: letters, numbers, _ . -';

  if (!fields.password)
    errors.password = 'Password is required.';
  else if (fields.password.length < PASSWORD_MIN)
    errors.password = `Password must be at least ${PASSWORD_MIN} characters.`;

  if (!fields.confirmPassword)
    errors.confirmPassword = 'Please confirm your password.';
  else if (fields.password !== fields.confirmPassword)
    errors.confirmPassword = 'Passwords do not match.';

  return errors;
}


interface PasswordInputProps {
  id: string;
  label: string;
  value: string;
  error?: string;
  disabled: boolean;
  onChange: (v: string) => void;
  onBlur: () => void;
}

const PasswordInput = ({ id, label, value, error, disabled, onChange, onBlur }: PasswordInputProps) => {
  const [visible, setVisible] = useState(false);

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className={`input pr-10 ${error ? 'border-red-400 focus:ring-red-200' : ''}`}
          autoComplete={id === 'password' ? 'new-password' : 'new-password'}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  );
};


interface TextFieldProps {
  id: keyof FormFields;
  label: string;
  value: string;
  error?: string;
  disabled: boolean;
  placeholder?: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}

const TextField = ({ id, label, value, error, disabled, placeholder, onChange, onBlur }: TextFieldProps) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} <span className="text-red-500">*</span>
    </label>
    <input
      id={id}
      type="text"
      value={value}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className={`input ${error ? 'border-red-400 focus:ring-red-200' : ''}`}
    />
    {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
  </div>
);


export const UserInviteLogin = ({ tempToken }: UserInviteLoginProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [fields, setFields] = useState<FormFields>({
    firstName: '',
    lastName: '',
    userName: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors]     = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError]   = useState('');
  const [done, setDone]                 = useState(false);


  const update = (key: keyof FormFields) => (value: string) => {
    setFields((f) => ({ ...f, [key]: value }));
    // Clear field error on change
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    if (submitError) setSubmitError('');
  };

  const blurValidate = (key: keyof FormFields) => () => {
    const fieldErrors = validate(fields);
    if (fieldErrors[key]) setErrors((e) => ({ ...e, [key]: fieldErrors[key] }));
  };

  const handleSubmit = async () => {
    const fieldErrors = validate(fields);
    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const responseData = await organizationInviteAPI.setPassword({
        firstName: fields.firstName.trim(),
        lastName:  fields.lastName.trim(),
        userName:  fields.userName.trim(),
        token:     tempToken,
        password:  fields.password,
      });
      
      const user = addNameField(responseData.user);
      
      localStorage.setItem(TOKEN_KEY, responseData.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, responseData.refresh_token);
      try { localStorage.setItem(USER_KEY, JSON.stringify(user)); } catch {}

      dispatch(setAuthCredentials({ user, token: responseData.access_token }));
      
      setDone(true);
      setTimeout(() => { navigate('/dashboard'); }, 1800);
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.detail ??
        err?.message ??
        'Something went wrong. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };


  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          <div className="flex flex-col items-center gap-4 text-center py-4 animate-in fade-in zoom-in-95 duration-300">
            <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">Account created!</h2>
              <p className="text-sm text-gray-500">Redirecting you to login…</p>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">

        {/* Header */}
        <div className="flex flex-col items-center gap-3 mb-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center">
            <UserCircle2 className="w-7 h-7 text-primary-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-1">Set up your account</h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              Create your profile to complete the invitation.
            </p>
          </div>
        </div>

        {/* Form fields */}
        <div className="space-y-4">

          {/* First name + Last name side by side */}
          <div className="grid grid-cols-2 gap-4">
            <TextField
              id="firstName"
              label="First Name"
              value={fields.firstName}
              error={errors.firstName}
              disabled={isSubmitting}
              placeholder="Jane"
              onChange={update('firstName')}
              onBlur={blurValidate('firstName')}
            />
            <TextField
              id="lastName"
              label="Last Name"
              value={fields.lastName}
              error={errors.lastName}
              disabled={isSubmitting}
              placeholder="Smith"
              onChange={update('lastName')}
              onBlur={blurValidate('lastName')}
            />
          </div>

          {/* Username */}
          <TextField
            id="userName"
            label="Username"
            value={fields.userName}
            error={errors.userName}
            disabled={isSubmitting}
            placeholder="jane_smith"
            onChange={update('userName')}
            onBlur={blurValidate('userName')}
          />

          {/* Password */}
          <PasswordInput
            id="password"
            label="Password"
            value={fields.password}
            error={errors.password}
            disabled={isSubmitting}
            onChange={update('password')}
            onBlur={blurValidate('password')}
          />

          {/* Confirm password */}
          <PasswordInput
            id="confirmPassword"
            label="Confirm Password"
            value={fields.confirmPassword}
            error={errors.confirmPassword}
            disabled={isSubmitting}
            onChange={update('confirmPassword')}
            onBlur={blurValidate('confirmPassword')}
          />

          {/* API-level submit error */}
          {submitError && (
            <p className="text-sm text-red-600 font-medium text-center animate-in fade-in slide-in-from-top-1 duration-200">
              {submitError}
            </p>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="btn btn-primary w-full flex items-center justify-center gap-2 min-h-[44px] mt-2"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Creating account…
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};